<template>
	<div
		class="home-page"
		:class="{
			'is-loading':
				isLoading || isCreatingAssemblyTransaction || isRemovingComponentTransaction || isAddingComponentTransaction,
			'mobile-detail-sheet-open': isMobileDetailSheetOpen,
		}"
		:style="{
			'--right-panel-open': isTableRightPanelOpen ? 1 : 0,
			'--left-panel-open': isLeftDrawerVisible ? 1 : 0,
			'--width-left-panel': 'var(--width-left-panel-layers)',
		}"
	>
		<!-- side nav -->
		<!-- v-show, not v-if: keeps .mobile-nav in the DOM so home-content/map sizing rules stay stable while the sheet is open -->
		<SideNav
			v-show="!isMobileSheetOpen"
			:current-user="currentUser"
			:store="store"
			:user-projects="userProjects"
			:current-project="currentProject"
			:is-user-logged-in="isUserLoggedIn"
			:notification-count="store.unreadNotificationCount"
			:mode="mode"
			:table-type="tableType"
			@open-project-modal="openProjectModal"
			@set-mode="setMode"
		/>

		<main class="home-content">
			<div v-if="accessDeniedMessage" class="access-denied-message">
				{{ accessDeniedMessage }}
			</div>

			<!-- left drawer -->
			<div v-show="canShowLeftDrawer" class="left-panel-container">
				<layers :current-map-style="currentMapStyle" @current-map-style="handleMapStyleUpdate" />
			</div>

			<!-- teleport target for panels that must float next to the left drawer, outside its overflow:hidden clipping -->
			<div id="floating-filter-panel-target"></div>
			<!-- floating filter reopen button -->
			<button
				v-if="isSidePanelLayout && !uiStore.isDrawerOpen && mode !== Mode.Table"
				class="filter-reopen-btn"
				:title="$t('subtitle-insc')"
				@click="uiStore.openDrawer()"
			>
				<AppIcon :name="IconName.Hexagons" />
			</button>

			<ViewToggle
				v-if="showViewToggle"
				class="view-toggle-floating"
				:model-value="mode"
				@update:model-value="handleHuntModeChange"
			/>

			<MobileLayersSheet
				v-if="showMobileLayersControl"
				v-model:open="isMobileLayersSheetOpen"
				:current-map-style="currentMapStyle"
				@current-map-style="handleMapStyleUpdate"
			/>

			<!-- map -->
			<div v-if="mode === Mode.Map" class="map-container">
				<Map
					ref="mapRef"
					:opportunity-filters="opportunityFilters"
					:current-style="currentMapStyle"
					:right-offset="mapControlsRightOffset"
					@current-style="handleMapStyleUpdate"
					@loaded="mapLoaded = true"
					@opportunity-selected="opportunitySelected"
					@layer-features="handleLayerFeatures"
					@error="(key, options) => toastStore.error(key, options)"
					@viewport-opportunities-changed="viewportOpportunities = $event"
					@viewport-opportunity-count-changed="viewportOpportunityCount = $event"
				/>

				<div v-if="!isSidePanelLayout && isTouchSelectionMode" class="selection-action-bar">
					<span>{{ $t("parcels.nSelected", { count: selectedParcels.length }, selectedParcels.length) }}</span>
					<button type="button" @click="mapRef?.exitSelectionMode?.()">
						<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
					</button>
				</div>

				<ViewportOpportunitiesPanel
					v-if="isShowingViewportPanel && isLargeScreen"
					:opportunities="viewportOpportunities"
					:total-count="viewportOpportunityCount"
					:project-name="currentProject ? projectDisplayName(currentProject, t) : undefined"
					:no-opportunity-layer-active="!hasActiveOpportunityLayer"
					@close="isShowingViewportPanel = false"
					@select="selectViewportOpportunity"
				/>
				<button
					v-else-if="!isShowingViewportPanel && isLargeScreen"
					class="viewport-panel-reopen-btn"
					:class="{ 'is-standalone': !showViewToggle }"
					@click="isShowingViewportPanel = true"
				>
					<AppIcon :name="IconName.Target" :size="16" />
					<span class="label">{{ $t("opportunities.viewportPanel.opportunities") }}</span>
					<span class="count">{{ viewportOpportunityCountLabel }}</span>
				</button>
			</div>

			<!-- Table -->
			<Table v-if="mode === Mode.Table" side-nav-overlay>
				<div v-if="tableType !== TableType.Buyers" class="table-type-selector is-label">
					<span class="table-type-label">
						{{ tableType === TableType.Transactions ? $t("table.transactions") : $t("table.opportunities") }}
					</span>
				</div>
				<BuyersList
					v-if="store.currentUser?.has(Permission.BuyerRead) && tableType === TableType.Buyers"
					:buyers="buyers"
					:loading="isLoadingBuyersList"
					:page="buyersListPage"
					:total="buyersTotal"
					@page-change="handleBuyersPageChange"
					@transaction-selection="handleBuyerTransactionSelection"
				/>
				<TransactionsTable
					v-else-if="store.currentUser?.has(Permission.TransactionRead) && tableType === TableType.Transactions"
				/>
				<OpportunitiesTable v-else />
			</Table>

			<!-- right drawer: redesigned opportunity card, in its own floating shell -->
			<DetailPanel
				v-if="isSidePanelLayout && panelStore.isOpen('opportunity') && mode !== Mode.Table"
				v-model="opportunityPanelCollapsed"
				width="var(--width-detail-panel)"
				:reopen-title="$t('callToActions.collapsePanel')"
				:reopen-top="detailPanelReopenTop"
			>
				<opportunity-card-detailed-v2
					:item="selectedOpportunityDetails || {}"
					:map-ref="mapRef"
					@make-ccv-success="toastStore.success('opportunities.makeCcvToast.success')"
					@collapse="panelStore.collapse()"
					@close="
						() => {
							panelStore.close('opportunity')
							mapRef?.clearSelections?.()
						}
					"
				/>
			</DetailPanel>

			<!-- right drawer: single-parcel card, in its own floating shell -->
			<DetailPanel
				v-else-if="isSidePanelLayout && selectedParcels.length === 1"
				v-model="parcelsPanelCollapsed"
				width="var(--width-detail-panel)"
				:reopen-title="$t('callToActions.collapsePanel')"
				:reopen-top="detailPanelReopenTop"
			>
				<parcel-card
					:item="selectedParcels[0]"
					:map-ref="mapRef"
					:canCreateQuickOpportunity="canCreateQuickOpportunity"
					@collapse="panelStore.collapse()"
					@close="mapRef?.clearSelections?.()"
				/>
			</DetailPanel>

			<!-- right drawer: transaction card, in its own floating shell -->
			<DetailPanel
				v-else-if="
					isSidePanelLayout &&
					(panelStore.isOpen('transaction') || store.isSelectedTransactionLoading) &&
					isUserLoggedIn &&
					currentUser.has(Permission.TransactionRead) &&
					config.ui.has(Feature.Transactions) &&
					(visibleLayers.find((id: string) => id === 'opportunities') || mode === Mode.Table)
				"
				v-model="transactionPanelCollapsed"
				:width="transactionPanelWidth"
				:reopen-title="$t('callToActions.collapsePanel')"
				:right-offset="secondaryPanelWidth"
				:reopen-top="detailPanelReopenTop"
			>
				<div v-if="!selectedTransaction && store.isSelectedTransactionLoading" class="transaction-skeleton">
					<div class="block price"></div>
					<div class="block address"></div>
					<div class="block borough"></div>
					<div class="block badge"></div>
					<div class="separator"></div>
					<div v-for="n in 10" :key="n" class="block row"></div>
				</div>
				<TransactionCardDetailed
					v-else-if="selectedTransaction"
					v-model:transaction="selectedTransaction"
					:key="selectedTransaction?.id || undefined"
					:is-loading="store.isSelectedTransactionLoading"
					@edit="isShowingComponentTransactionsPanel = true"
					@list-add="isShowingComponentTransactionsPanel = false"
					@collapse="panelStore.collapse()"
					@close="
						() => {
							panelStore.close('transaction')
							selectedTransaction = null
						}
					"
					@update:edit-mode="isEditingSelectedTransaction = $event"
				/>
			</DetailPanel>

			<!-- right drawer: multi-selected parcels card, in its own floating shell -->
			<DetailPanel
				v-else-if="isLargeScreen && panelStore.isOpen('parcels')"
				v-model="parcelsPanelCollapsed"
				width="var(--width-detail-panel)"
				:reopen-title="$t('callToActions.collapsePanel')"
				:reopen-top="detailPanelReopenTop"
			>
				<parcels-card-detailed
					:items="selectedParcels"
					:canCreateDetailedOpportunity="canCreateDetailedOpportunity"
					:canCreateQuickOpportunity="canCreateQuickOpportunity"
					:map-ref="mapRef"
					@collapse="panelStore.collapse()"
					@close="
						() => {
							panelStore.close('parcels')
							mapRef?.clearSelections?.()
						}
					"
				/>
			</DetailPanel>

			<!-- right drawer: map layer feature details, in its own floating shell -->
			<DetailPanel
				v-else-if="
					isLargeScreen &&
					layerFeatureDetails.length > 0 &&
					(visibleLayers.find((id: string) => ['opportunities', LayerType.CompletedAssessments].includes(id)) ||
						mode === Mode.Table)
				"
				v-model="layersPanelCollapsed"
				width="var(--width-detail-panel)"
				:reopen-title="$t('callToActions.collapsePanel')"
				:reopen-top="detailPanelReopenTop"
			>
				<LayersDetailsPanel
					:details="layerFeatureDetails"
					@collapse="panelStore.collapse()"
					@close="handleLayerFeatures([])"
				/>
			</DetailPanel>

			<!-- right drawer - only when something is selected -->
			<div
				v-else-if="
					isLargeScreen &&
					panelStore.isOpen('opportunity') &&
					(visibleLayers.find((id: string) => id === 'opportunities') || mode === Mode.Table)
				"
				class="right-panel-container drawer-open"
				:class="{ 'with-secondary-panel': isShowingSecondaryRightPanel }"
				:style="isShowingSecondaryRightPanel ? { right: secondaryPanelWidth } : undefined"
			>
				<div class="right-drawer-panel">
					<div class="content">
						<!-- opportunity details -->
						<div class="detail-panel">
							<div class="panel-close-btn" @click="panelStore.close('opportunity')">
								<AppIcon :name="IconName.CloseSmall" :size="24" :stroke-width="1.2" />
							</div>
							<opportunity-card-detailed-v2
								:item="
									(mode === Mode.Table
										? selectedOpportunity
										: opportunities.find((l: Opportunity) => l.id === String(selectedOpportunityId))) || {}
								"
								:map-ref="mapRef"
								@make-ccv-success="toastStore.success('opportunities.makeCcvToast.success')"
							/>
						</div>
					</div>
				</div>
			</div>

			<!-- component transactions (of an assembly transaction) -->
			<AssemblyPanel
				v-if="isComponentTransactionsPanelVisible"
				v-model:inscription-number="componentTransactionInscriptionNumber"
				:components="selectedTransaction?.components ?? []"
				:adding="isAddingComponentTransaction"
				:removing="isRemovingComponentTransaction"
				:error-message="componentTransactionErrorMessage"
				@add="addComponentTransaction"
				@remove="removeComponentTransaction"
				@clear-error="componentTransactionErrorMessage = ''"
				@close="isShowingComponentTransactionsPanel = false"
			/>

			<!-- multi-transactions -->
			<TransactionsPanel
				v-if="isShowingTransactionExportPanel"
				:creating-assembly="isCreatingAssemblyTransaction"
				@create-assembly="createAssemblyTransaction"
			/>

			<!-- teleported to body so position:fixed isn't clipped to .home-content's nav-bar-reduced box on mobile -->
			<Teleport to="body">
				<!-- redesigned opportunity card as a mobile bottom sheet -->
				<MobileDrawer
					v-if="!isSidePanelLayout"
					v-model="opportunityPanelCollapsed"
					:active="panelStore.isOpen('opportunity') && !!opportunityPeekSnapPoint"
					:peek-height="opportunityPeekSnapPoint ?? '0px'"
					:reopen-title="$t('callToActions.reopenPanel')"
					@dismiss="closeOpportunityDialog"
					v-slot="{ expanded }"
				>
					<opportunity-card-detailed-v2
						v-if="selectedOpportunity"
						id="opportunity-card-detailed"
						compact
						:expanded="expanded"
						:item="selectedOpportunity || {}"
						:map-ref="mapRef"
						@make-ccv-success="toastStore.success('opportunities.makeCcvToast.success')"
						@collapse="panelStore.collapse()"
						@close="closeOpportunityDialog"
						@header-resize="onOpportunityHeaderResize"
					/>
				</MobileDrawer>

				<MobileDrawer
					v-if="!isSidePanelLayout"
					v-model="parcelsPanelCollapsed"
					:active="selectedParcels.length === 1 && !isTouchSelectionMode && !!parcelPeekSnapPoint"
					:peek-height="parcelPeekSnapPoint ?? '0px'"
					:reopen-title="$t('callToActions.reopenPanel')"
					@dismiss="mapRef?.exitSelectionMode?.()"
				>
					<parcel-card
						v-if="selectedParcels.length === 1 && !isTouchSelectionMode"
						id="parcel-card"
						compact
						:item="selectedParcels[0]"
						:map-ref="mapRef"
						:canCreateQuickOpportunity="canCreateQuickOpportunity"
						@collapse="panelStore.collapse()"
						@close="mapRef?.exitSelectionMode?.()"
						@header-resize="onParcelHeaderResize"
					/>
				</MobileDrawer>

				<!-- touch selection mode: selected parcels bottom sheet (1+ lots once selection mode is active) -->
				<MobileDrawer
					v-if="!isSidePanelLayout"
					v-model="parcelsPanelCollapsed"
					:active="selectedParcels.length >= 1 && isTouchSelectionMode && !!parcelPeekSnapPoint"
					:peek-height="parcelPeekSnapPoint ?? '0px'"
					:reopen-title="$t('callToActions.reopenPanel')"
					@dismiss="mapRef?.exitSelectionMode?.()"
					v-slot="{ expanded }"
				>
					<parcels-card-detailed
						v-if="selectedParcels.length >= 1 && isTouchSelectionMode"
						compact
						:expanded="expanded"
						:items="selectedParcels"
						:canCreateQuickOpportunity="canCreateQuickOpportunity"
						:canCreateDetailedOpportunity="canCreateDetailedOpportunity"
						:map-ref="mapRef"
						@collapse="panelStore.collapse()"
						@close="mapRef?.exitSelectionMode?.()"
						@header-resize="onParcelHeaderResize"
					/>
				</MobileDrawer>

				<MobileDrawer
					v-if="!isSidePanelLayout"
					v-model="transactionPanelCollapsed"
					:active="panelStore.isOpen('transaction') && !!transactionPeekSnapPoint"
					:peek-height="transactionPeekSnapPoint ?? '0px'"
					:reopen-title="$t('callToActions.reopenPanel')"
					@dismiss="selectedTransaction = null"
				>
					<TransactionCardDetailed
						v-if="selectedTransaction"
						id="transaction-card-detailed"
						compact
						v-model:transaction="selectedTransaction"
						:key="selectedTransaction?.id || undefined"
						:is-loading="store.isSelectedTransactionLoading"
						@edit="isShowingComponentTransactionsPanel = true"
						@list-add="isShowingComponentTransactionsPanel = false"
						@collapse="panelStore.collapse()"
						@close="selectedTransaction = null"
						@header-resize="onTransactionHeaderResize"
					/>
				</MobileDrawer>
			</Teleport>
		</main>

		<!-- Should we add all the modals here? -->
		<!-- Project Modal -->
		<ProjectModal
			:is-open="isProjectModalOpen"
			:current-project="currentProject?.type !== ProjectType.Workspace ? currentProject : null"
			@close="closeProjectModal"
		/>
	</div>
</template>

<script lang="ts" setup>
import { useRouter, useRoute } from "vue-router"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { firstQueryValue } from "@/utils/routing"
import { type Ref, computed, ref, inject, onMounted, onUnmounted, watch, toRaw } from "vue"
import { useI18n } from "vue-i18n"

import MobileDrawer from "@/components/layout/MobileDrawer.vue"
import MobileLayersSheet from "@/components/layers/MobileLayersSheet.vue"

import Map from "@/components/map/Map.vue"
import Layers from "@/components/layers/Layers.vue"
import { MAP_STYLES, type MapStyleValue } from "@/components/constants"

import { UseRootStore, Permission, RedirectReason, Mode, TableType, isTableType } from "@/model/RootStore"
import { ProjectType, OpportunityType } from "@/graphql"
import { storeToRefs } from "pinia"
import { ProjectStoreKey, isHuntingMode } from "@/stores/projectStore.ts"
import { useNavigationContext } from "@/composables/useNavigationContext"
import OpportunityCardDetailedV2 from "@/components/opportunities/OpportunityCardDetailedV2.vue"
import DetailPanel from "@/components/layout/DetailPanel.vue"
import ViewportOpportunitiesPanel from "@/components/opportunities/ViewportOpportunitiesPanel.vue"
import TransactionCardDetailed from "@/components/transactions/TransactionCardDetailed.vue"
import ParcelsCardDetailed from "@/components/parcels/ParcelsCardDetailed.vue"
import ParcelCard from "@/components/parcels/ParcelCard.vue"
import TransactionsPanel from "@/components/transactions/TransactionsPanel.vue"
import AssemblyPanel from "@/components/transactions/AssemblyPanel.vue"
import type { Transaction, Opportunity, Contact } from "@/model/DataModel.ts"
import { LayerType } from "@/model/DataModel.ts"
import { useMediaQuery } from "@vueuse/core"
import { config, Feature } from "@/AppConfig.ts"
import { isExpired } from "@/utils/date"
import { projectDisplayName } from "@/utils/project"
import ProjectModal from "@/components/modals/ProjectModal.vue"

import { useUIStore } from "@/stores/uiStore"
import { useToastStore } from "@/stores/toastStore"
import { useDetailPanelStore, type DetailPanelType } from "@/stores/detailPanelStore"
import { useBuyerStore } from "@/stores/buyerStore"
import { useSearchContextStore } from "@/stores/searchContextStore"
import { useView } from "@/composables/useView"
import { ViewScope } from "@/types"
import SideNav from "@/components/layout/SideNav.vue"
import LayersDetailsPanel from "@/components/layers/LayersDetailsPanel.vue"
import { useDetailPanelAutoClose } from "@/composables/useDetailPanelAutoClose"
import ViewToggle from "@/components/layout/ViewToggle.vue"
import Table from "@/components/table/Table.vue"
import BuyersList from "@/components/table/BuyersList.vue"
import OpportunitiesTable from "@/components/table/OpportunitiesTable.vue"
import TransactionsTable from "@/components/table/TransactionsTable.vue"
import { PAGINATION_SIZE } from "@/components/constants"
import type { LayerFeatureDetail } from "@/model/DataModel"

const uiStore = useUIStore()
const panelStore = useDetailPanelStore()
const buyerStore = useBuyerStore()
const searchContextStore = useSearchContextStore()

const store = UseRootStore()
const {
	visibleLayers,
	isUserLoggedIn,
	opportunities,
	selectedOpportunityId,
	selectedParcels,
	selectedTransaction,
	transactionExportList,
	currentUser,
	userProjects,
	filters,
} = storeToRefs(store)

const router = useRouter()
const route = useRoute()

const projectStore = inject(ProjectStoreKey)!()
const buyersView = useView(ViewScope.BuyersTable)
const { currentProject } = storeToRefs(projectStore)
const showViewToggle = computed(() => isLargeScreen.value && firstQueryValue(route.query.project) != null)

const opportunityFilters = ref({})
const mapLoaded = ref(false)
const isLargeScreen = inject<Ref<boolean>>("isLargeScreen")!
const selectedOpportunity = ref<Opportunity | null>(null)
const selectedOpportunityDetails = computed(() => {
	if (selectedOpportunityId.value == null) return null
	if (selectedOpportunity.value && String(selectedOpportunity.value.id) === String(selectedOpportunityId.value)) {
		return selectedOpportunity.value
	}
	return opportunities.value.find(
		(opportunity: Opportunity) => String(opportunity.id) === String(selectedOpportunityId.value)
	)
})
const selectedOpportunityDialog = ref<HTMLDialogElement | null>(null)
// null = not yet measured for the current transaction; the drawer stays collapsed until it is (see :active)
const transactionPeekSnapPoint = ref<string | null>(null)
function onTransactionHeaderResize(height: number) {
	if (height > 0) {
		transactionPeekSnapPoint.value = `${height}px`
	}
}
// null = not yet measured for the current parcel; the drawer stays collapsed until it is (see :active)
const parcelPeekSnapPoint = ref<string | null>(null)
function onParcelHeaderResize(height: number) {
	if (height > 0) {
		parcelPeekSnapPoint.value = `${height}px`
	}
}
// null = not yet measured for the current opportunity; the drawer stays collapsed until it is (see :active)
const opportunityPeekSnapPoint = ref<string | null>(null)
function onOpportunityHeaderResize(height: number) {
	if (height > 0) {
		opportunityPeekSnapPoint.value = `${height}px`
	}
}
const mapRef = ref()
const isTouchSelectionMode = computed(() => !!mapRef.value?.selectionModeActive)

// mobile-only "Couches" bottom sheet (see MobileLayersSheet.vue)
const isMobileLayersSheetOpen = ref(false)
// dismiss detail sheets that are actively showing; a collapsed one stays as its reopen button
function dismissMobileDetailSheets(): void {
	if (panelStore.isOpen("opportunity") && !panelStore.isCollapsed("opportunity")) {
		closeOpportunityDialog()
	}
	if (panelStore.isOpen("transaction") && !panelStore.isCollapsed("transaction")) {
		selectedTransaction.value = null
	}
	if (selectedParcels.value.length > 0 && !panelStore.isCollapsed("parcels")) {
		selectedParcels.value = []
	}
}
watch(isMobileLayersSheetOpen, open => {
	if (open) dismissMobileDetailSheets()
})

// writable proxies so DetailPanel/MobileDrawer can `v-model` straight onto the panel store
function collapsedModel(type: DetailPanelType) {
	return computed({
		get: () => panelStore.isCollapsed(type),
		set: value => (value ? panelStore.collapse() : panelStore.expand()),
	})
}
const opportunityPanelCollapsed = collapsedModel("opportunity")
const parcelsPanelCollapsed = collapsedModel("parcels")
const transactionPanelCollapsed = collapsedModel("transaction")
const layersPanelCollapsed = collapsedModel("layers")

const isShowingComponentTransactionsPanel = ref(false)
const isShowingViewportPanel = ref(false)
const viewportOpportunities = ref<Opportunity[]>([])
const viewportOpportunityCount = ref(0)
const viewportOpportunityCountLabel = computed(() =>
	viewportOpportunityCount.value >= 100 ? "100+" : String(viewportOpportunityCount.value)
)
// SideNav's viewport-panel button/panel claims the top slot only on large screens; below that it isn't rendered, so nothing does
const defaultReopenTop = computed(() =>
	mode.value === Mode.Table || !isLargeScreen.value ? "var(--spacing-md)" : undefined
)
// clears whichever top-right floating buttons are actually stacked above it: the Map/Table
// ViewToggle (hunting only) and the viewport panel's reopen button (while the panel is collapsed)
const detailPanelReopenTop = computed(() => {
	const hasViewportReopenBtn = isLargeScreen.value && !isShowingViewportPanel.value
	const stackedSlots = (showViewToggle.value ? 1 : 0) + (hasViewportReopenBtn ? 1 : 0)
	if (stackedSlots === 0) return defaultReopenTop.value
	return `calc(var(--spacing-md) + (${stackedSlots} * var(--space-40)) + (${stackedSlots} * var(--spacing-floating-btn-stack-gap)))`
})

const layerFeatureDetails = ref<LayerFeatureDetail[]>([])
const isSideNavBottomBar = useMediaQuery("(max-width: 768px)")
const isSidePanelLayout = computed(() => isLargeScreen.value || !isSideNavBottomBar.value)
// the left Layers panel has no room on mobile; surface it as a floating button + bottom sheet
const showMobileLayersControl = computed(() => !isSidePanelLayout.value && mode.value === Mode.Map)
// derived (not imperatively toggled) so it can't drift out of sync with which mobile sheet is showing
const isMobileDetailSheetOpen = computed(() => {
	if (isSidePanelLayout.value) return false
	if (panelStore.isOpen("opportunity") && !panelStore.isCollapsed("opportunity")) return true
	// covers both the single-parcel drawer and the touch-selection multi-parcel drawer
	if (selectedParcels.value.length >= 1 && !panelStore.isCollapsed("parcels")) {
		return true
	}
	return panelStore.isOpen("transaction") && !panelStore.isCollapsed("transaction")
})
const isMobileSheetOpen = computed(
	() => !isSidePanelLayout.value && (isMobileLayersSheetOpen.value || isMobileDetailSheetOpen.value)
)
watch(isMobileDetailSheetOpen, open => {
	if (open) isMobileLayersSheetOpen.value = false
})
const isProjectModalOpen = ref(false)
const accessDeniedMessage = ref("")
const toastStore = useToastStore()
const { t } = useI18n()
const routeTableType = computed(() => firstQueryValue(route.query.table))
const mode = computed(() => (routeTableType.value ? Mode.Table : Mode.Map))

const opportunityMapView = useView(ViewScope.OpportunitiesMap)
const opportunityHuntingMapView = useView(ViewScope.OpportunitiesHuntingMap)
const { isMapMode: isNavigationMapMode, isHuntingSection } = useNavigationContext({ currentProject })
const isHuntingMapMode = computed(() => isNavigationMapMode.value && isHuntingSection.value)
const activeOpportunityMapView = computed(() =>
	isHuntingMapMode.value ? opportunityHuntingMapView : opportunityMapView
)
const defaultOpportunityMapTypes = computed(() =>
	isHuntingMapMode.value
		? [
				OpportunityType.ListingLanderz,
				OpportunityType.ListingPotential,
				OpportunityType.OffMarketOfficial,
				OpportunityType.OpportunityTarget,
			]
		: [OpportunityType.ListingLanderz, OpportunityType.ListingPotential, OpportunityType.OffMarketOfficial]
)
const visibleOpportunityTypes = computed(() => {
	const types =
		activeOpportunityMapView.value.view.value.filter?.opportunity?.opportunityTypes ?? defaultOpportunityMapTypes.value
	return isHuntingMode() ? types : types.filter(type => type !== OpportunityType.OpportunityTarget)
})

useDetailPanelAutoClose({ store, panelStore, mapRef, layerFeatureDetails, mode, visibleOpportunityTypes })

const opportunityLayerIds: string[] = [
	LayerType.ListingLanderz,
	LayerType.ListingPotential,
	LayerType.OffMarketOfficial,
	LayerType.OpportunityTarget,
]
const hasActiveOpportunityLayer = computed(() => filters.value.layers.some(id => opportunityLayerIds.includes(id)))
const isLeftDrawerVisible = computed(() => uiStore.isDrawerOpen && mode.value !== Mode.Table)
const canShowLeftDrawer = computed(() => isLeftDrawerVisible.value && isSidePanelLayout.value)
const isOpportunityDetailsPanelShowing = computed(
	() =>
		panelStore.isOpen("opportunity") &&
		// the redesigned DetailPanel never renders in table mode, so it shouldn't reserve table space either
		mode.value !== Mode.Table
)
const isAnyLeftDetailPanelShowing = computed(
	() =>
		(isOpportunityDetailsPanelShowing.value && !panelStore.isCollapsed("opportunity")) ||
		(panelStore.isOpen("parcels") && !panelStore.isCollapsed("parcels")) ||
		(panelStore.isOpen("transaction") && !panelStore.isCollapsed("transaction")) ||
		(panelStore.isOpen("layers") && !panelStore.isCollapsed("layers"))
)
const isTableRightPanelOpen = computed(() => mode.value === Mode.Table && isAnyLeftDetailPanelShowing.value)

watch(mode, newMode => {
	if (newMode === Mode.Table) {
		uiStore.closeDrawer()
	} else {
		uiStore.openDrawer()
	}
})
const buyers = ref<Contact[]>([])
const buyersListPage = computed({
	get: () => store.buyersListPage,
	set: page => {
		store.buyersListPage = page
	},
})
const buyersTotal = ref(0)
let buyersRequestId = 0
const isLoading = ref(false)
const isLoadingBuyersList = ref(false)
const isCreatingAssemblyTransaction = ref(false)
const isRemovingComponentTransaction = ref(false)
const isAddingComponentTransaction = ref(false)
const componentTransactionInscriptionNumber = ref("")
const componentTransactionErrorMessage = ref("")
const tableType = computed<TableType>({
	get() {
		const fromRoute = routeTableType.value
		if (isTableType(fromRoute)) {
			return fromRoute
		}

		return store.storedTableType
	},
	set(value) {
		void router.replace({ query: { ...route.query, table: value } })
	},
})

function setMode(newMode: Mode, newTableType?: TableType): void {
	const query = { ...route.query }
	if (newMode === Mode.Table) {
		const current = tableType.value
		query.table = newTableType ?? (current === TableType.Buyers ? store.storedTableType : current)
	} else {
		delete query.table
	}

	void router.replace({ query })
}

function handleHuntModeChange(newMode: Mode): void {
	setMode(newMode, newMode === Mode.Table ? TableType.Opportunities : undefined)
}

function handleLayerFeatures(details: LayerFeatureDetail[]) {
	layerFeatureDetails.value = details
	if (details.length > 0) {
		panelStore.open("layers")
	} else {
		panelStore.close("layers")
	}
}

onMounted(async () => {
	;(window as any).mode = {
		get: () => toRaw(mode.value),
		set: (v: string) => {
			if (v === Mode.Map) {
				setMode(Mode.Map)
			}

			if (v === Mode.Table) {
				setMode(Mode.Table)
			}
		},
	}

	// TODO move to AppRouter
	const projectId = route.query.project as string | undefined
	const pendingEvaluation = route.query.pendingEvaluation as string | undefined

	if (!isUserLoggedIn.value && projectId && config.ui.has(Feature.ProjectAccessControl)) {
		router.push(`/login?redirect=/?project=${projectId}&reason=${RedirectReason.LoginRequired}`)
		return
	}

	if (isUserLoggedIn.value) {
		try {
			await store.fetchCurrentUserDetails({ includeProjectPeople: false })

			if (pendingEvaluation) {
				const [status, opportunityId] = pendingEvaluation.split(":")
				if (status && opportunityId) {
					try {
						await store.updateOpportunityEvaluation(Number(opportunityId), { status })
						toastStore.success("notifications.evaluationSaved")
					} catch (err) {
						console.error("Failed to replay pending evaluation:", err)
					}
				}
				router.replace({ query: { ...route.query, pendingEvaluation: undefined } })
			}
		} catch (error: any) {
			const errorMessage = error?.message || error?.messages?.[0] || String(error)
			if (errorMessage.includes("Token has been revoked")) {
				return
			}
		}
	}

	try {
		const project = await projectStore.fetchCurrentProjectMetadata({ includePeople: false })
		if (project.value == null) {
			return
		}

		// Workspaces have no project-owned search context; only the user's live or saved context applies.
		if (project.value.type === ProjectType.Workspace) {
			store.rehydrateLocalFiltersForWorkspace()
		} else {
			if (project.value.searchContext) {
				searchContextStore.applySearchContext(project.value.searchContext)
				if (project.value.searchContext.layers.length === 0) {
					store.setVisibleLayers(store.defaultVisibleLayers)
				}
			}
			searchContextStore.markProjectSearchContextLoaded()
		}

		if (config.ui.has(Feature.ProjectAccessControl)) {
			if (project.value.expiryDate) {
				const expiryDate = new Date(project.value.expiryDate)
				if (isExpired(expiryDate)) {
					accessDeniedMessage.value = t("project.expired-message")
					return
				}
			}
		}
	} catch (error: any) {
		if (config.ui.has(Feature.ProjectAccessControl)) {
			const errorMessage = error?.message || error?.messages?.[0] || String(error)
			if (errorMessage.includes("expired")) {
				accessDeniedMessage.value = t("project.expired-message")
			} else if (errorMessage.includes("not allowed to view this project")) {
				accessDeniedMessage.value = t("project.access-denied-message")
			} else {
				console.error("Error fetching project:", error)
			}
		} else {
			console.error("Error fetching project:", error)
		}
	}
})

onUnmounted(() => {
	delete (window as any).mode
})

watch(currentProject, project => {
	if (store.isUserLoggedIn) {
		store.fetchOpportunityEvaluations(project?.id)
	}
})

const opportunitySelected = (opportunity: Opportunity) => {
	// closes the transaction sheet/panel without deselecting it, so it stays correctly sized when reopened
	panelStore.close("transaction")
	selectedOpportunity.value = opportunity
	panelStore.open("opportunity")
	layerFeatureDetails.value = []
	// legacy full-screen modal path; the redesigned card is a MobileDrawer bottom sheet driven by panelStore
	if (selectedOpportunityDialog.value) {
		// showModal() throws if already open; re-selecting while it's open just swaps the content
		if (!selectedOpportunityDialog.value.open) {
			selectedOpportunityDialog.value.showModal()
		}
		selectedOpportunityDialog.value.removeAttribute("hidden")
	}
}

const selectViewportOpportunity = (opportunity: Opportunity) => {
	selectedOpportunityId.value = opportunity.id
	opportunitySelected(opportunity)
}

const currentMapStyle = ref<MapStyleValue>(
	(window.localStorage.getItem("map-style") as MapStyleValue) || MAP_STYLES.SIMPLE
)

const closeOpportunityDialog = () => {
	if (selectedOpportunityDialog.value) {
		selectedOpportunityDialog.value.close()
		selectedOpportunityDialog.value.setAttribute("hidden", "")
	}
	selectedOpportunityId.value = null
}

const handleMapStyleUpdate = (name: MapStyleValue) => {
	currentMapStyle.value = name
	window.localStorage.setItem("map-style", name)
}

async function openProjectModal() {
	if (currentProject.value?.type !== ProjectType.Workspace) {
		try {
			await projectStore.fetchCurrentProjectMetadata({ includePeople: true })
		} catch (error) {
			console.error("Failed to fetch editable project details:", error)
		}
	}
	isProjectModalOpen.value = true
}

function closeProjectModal() {
	isProjectModalOpen.value = false
}

const canCreateDetailedOpportunity = computed(
	() =>
		isUserLoggedIn.value &&
		currentUser.value.has(Permission.OpportunityTargetWrite) &&
		config.ui.has(Feature.AddOpportunityFull)
)
const canCreateQuickOpportunity = computed(
	() =>
		isUserLoggedIn.value &&
		currentUser.value.has(Permission.OpportunityTargetWrite) &&
		config.ui.has(Feature.AddOpportunity)
)
const isShowingTransactionExportPanel = computed(() => {
	return (
		isUserLoggedIn.value &&
		currentUser.value.has(Permission.TransactionRead) &&
		transactionExportList.value.size > 0 &&
		!isShowingComponentTransactionsPanel.value
	)
})
const isComponentTransactionsPanelVisible = computed(() => {
	return (
		currentUser.value.has(Permission.TransactionWrite) &&
		isShowingComponentTransactionsPanel.value &&
		selectedTransaction.value != null
	)
})
const isShowingSecondaryRightPanel = computed(() => {
	return isShowingTransactionExportPanel.value || isComponentTransactionsPanelVisible.value
})

const secondaryPanelWidth = computed(() => {
	if (isShowingTransactionExportPanel.value) return "var(--width-transaction-export-panel)"
	if (isComponentTransactionsPanelVisible.value) return "var(--width-transaction-export-panel)"
	return "0px"
})

// assembly transactions have a denser UI; while editing one, keep the panel wider instead of shrinking on small screens
const isSelectedTransactionAssembly = computed(() => (selectedTransaction.value?.components?.length ?? 0) > 0)
const isEditingSelectedTransaction = ref(false)
const transactionPanelWidth = computed(() =>
	isSelectedTransactionAssembly.value && isEditingSelectedTransaction.value
		? "clamp(360px, 20.8333vw, 400px)"
		: "var(--width-detail-panel)"
)

const isViewportPanelShowing = computed(() => isShowingViewportPanel.value && isLargeScreen.value)

const mapControlsRightOffset = computed(() => {
	if (!isLargeScreen.value) return "0px" // detail panel is a full-screen drawer on mobile, not a side panel
	const isTransactionPanelShowing = panelStore.isOpen("transaction") && !panelStore.isCollapsed("transaction")
	const detailWidth = isAnyLeftDetailPanelShowing.value
		? `calc(${isTransactionPanelShowing ? transactionPanelWidth.value : "var(--width-detail-panel)"} + ${secondaryPanelWidth.value})`
		: "0px"
	const viewportWidth = isViewportPanelShowing.value ? "var(--width-viewport-opportunities-panel)" : "0px"
	return `max(${detailWidth}, ${viewportWidth})`
})

type PaginationChangeDetails = {
	page: number
	pageSize: number
	offset: number
}

async function handleBuyerTransactionSelection(inscriptionNumber: string | null | undefined): Promise<void> {
	if (!inscriptionNumber) return

	isLoading.value = true
	store.isSelectedTransactionLoading = true
	try {
		const transaction = await store.getTransactionByInscriptionNumber(inscriptionNumber)
		if (!transaction) return

		store.addSelectedTransaction(transaction)
	} finally {
		isLoading.value = false
		store.isSelectedTransactionLoading = false
	}
}

async function handleBuyersPageChange(details: PaginationChangeDetails): Promise<void> {
	buyersListPage.value = details.page

	await refreshBuyers()
}

async function createAssemblyTransaction(transactions: Transaction[]): Promise<void> {
	if (isCreatingAssemblyTransaction.value) return

	isCreatingAssemblyTransaction.value = true

	const componentInscriptionNumbers = transactions
		.map(transaction => transaction.inscriptionNumber)
		.filter((inscriptionNumber): inscriptionNumber is string => inscriptionNumber != null)

	try {
		const transaction = await store.createLandAssemblyTransaction(componentInscriptionNumbers)

		store.clearTransactionExportList()
		store.addSelectedTransaction(transaction)
	} finally {
		isCreatingAssemblyTransaction.value = false
	}
}

async function removeComponentTransaction(componentTransaction: Transaction): Promise<void> {
	if (isRemovingComponentTransaction.value) return

	const assembly = selectedTransaction.value
	const assemblyInscriptionNumber = assembly?.inscriptionNumber

	if (!assemblyInscriptionNumber || !assembly.components) return

	const componentInscriptionNumbers = assembly.components
		.filter((transaction: Transaction) => transaction.inscriptionNumber !== componentTransaction.inscriptionNumber)
		.map((transaction: Transaction) => transaction.inscriptionNumber)
		.filter((inscriptionNumber: string | null | undefined): inscriptionNumber is string => inscriptionNumber != null)

	const overrides = { componentInscriptionNumbers } as Partial<Transaction> & { componentInscriptionNumbers: string[] }

	isRemovingComponentTransaction.value = true

	try {
		const transaction = await store.updateTransaction(assemblyInscriptionNumber, overrides)
		store.addSelectedTransaction(transaction)
	} finally {
		isRemovingComponentTransaction.value = false
	}
}

async function addComponentTransaction(): Promise<void> {
	if (isAddingComponentTransaction.value || isRemovingComponentTransaction.value) return

	const assembly = selectedTransaction.value
	const assemblyInscriptionNumber = assembly?.inscriptionNumber
	const componentInscriptionNumber = componentTransactionInscriptionNumber.value.trim()

	if (!assemblyInscriptionNumber || !componentInscriptionNumber) return

	if (componentInscriptionNumber === assemblyInscriptionNumber) {
		componentTransactionErrorMessage.value = t("landAssembly.cannotAddSelf")
		return
	}

	const existingComponentInscriptionNumbers = (assembly.components ?? [])
		.map((transaction: Transaction) => transaction.inscriptionNumber)
		.filter((inscriptionNumber: string | null | undefined): inscriptionNumber is string => inscriptionNumber != null)

	if (existingComponentInscriptionNumbers.includes(componentInscriptionNumber)) {
		componentTransactionErrorMessage.value = t("landAssembly.alreadyComponent")
		return
	}

	const overrides = {
		componentInscriptionNumbers: [...existingComponentInscriptionNumbers, componentInscriptionNumber],
	} as Partial<Transaction> & { componentInscriptionNumbers: string[] }

	isAddingComponentTransaction.value = true
	componentTransactionErrorMessage.value = ""

	try {
		const transaction = await store.updateTransaction(assemblyInscriptionNumber, overrides)
		store.addSelectedTransaction(transaction)
		componentTransactionInscriptionNumber.value = ""
	} catch (error: any) {
		componentTransactionErrorMessage.value = error?.message || t("landAssembly.addFailed")
	} finally {
		isAddingComponentTransaction.value = false
	}
}

watch(
	() => buyersView.view.value,
	async () => {
		if (mode.value !== Mode.Table || tableType.value !== TableType.Buyers) {
			return
		}

		buyersListPage.value = 1

		await refreshBuyers()
	},
	{ deep: true, immediate: false }
)

watch(
	[mode, tableType],
	async () => {
		await refreshBuyers()
	},
	{ immediate: true }
)

async function refreshBuyers(): Promise<void> {
	if (mode.value !== Mode.Table || tableType.value !== TableType.Buyers) {
		isLoadingBuyersList.value = false

		return
	}

	const requestId = ++buyersRequestId
	isLoadingBuyersList.value = true
	const offset = (buyersListPage.value - 1) * PAGINATION_SIZE

	try {
		const result = await buyerStore.fetchByView(buyersView.view.value, { limit: PAGINATION_SIZE, offset })

		if (requestId !== buyersRequestId || mode.value !== Mode.Table || tableType.value !== TableType.Buyers) {
			return
		}

		buyers.value = Array.isArray(result.items) ? result.items : []
		buyersTotal.value = result.total ?? buyers.value.length
	} finally {
		if (requestId === buyersRequestId) {
			isLoadingBuyersList.value = false
		}
	}
}

watch(selectedTransaction, (newState, oldState) => {
	const isTransactionSelected = Boolean(newState)
	const isSameTransaction = newState?.inscriptionNumber === oldState?.inscriptionNumber

	if (!isSameTransaction) {
		isShowingComponentTransactionsPanel.value = false
		componentTransactionInscriptionNumber.value = ""
		componentTransactionErrorMessage.value = ""
	}

	if (isTransactionSelected) {
		panelStore.open("transaction")
		selectedParcels.value = []
		layerFeatureDetails.value = []
		closeOpportunityDialog()
		if (!isSameTransaction) {
			transactionPeekSnapPoint.value = null
		}
	} else {
		panelStore.close("transaction")
	}
})

watch(
	() => store.isSelectedTransactionLoading,
	isLoading => {
		if (isLoading) {
			panelStore.open("transaction")
		} else if (!selectedTransaction.value) {
			panelStore.close("transaction")
		}
	}
)

watch(
	selectedParcels,
	newState => {
		const isAnyParcelSelected = Boolean(newState.length > 0)
		if (isAnyParcelSelected) {
			panelStore.open("parcels")
			layerFeatureDetails.value = []
			store.clearTransactionExportList()
			closeOpportunityDialog()
		} else {
			panelStore.close("parcels")
		}
	},
	{ deep: true }
)

watch(selectedOpportunityId, (newState, oldState) => {
	const isOpportunitySelected = Boolean(newState)
	if (isOpportunitySelected) {
		panelStore.open("opportunity")
		selectedParcels.value = []
		layerFeatureDetails.value = []
		if (newState !== oldState) {
			opportunityPeekSnapPoint.value = null
		}
	} else {
		panelStore.close("opportunity")
	}
})
</script>

<style scoped>
@keyframes skeleton-blink {
	50% {
		opacity: 0.2;
	}
}

.transaction-skeleton {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);
	padding: var(--spacing-card-padding-sm);

	.block {
		border-radius: var(--border-radius-base);
		background-color: var(--neutral-200);
		animation: skeleton-blink 1.4s ease-in-out infinite;
	}

	.price {
		width: 140px;
		height: 28px;
	}

	.address {
		width: 70%;
		height: 18px;
	}

	.borough {
		width: 45%;
		height: 14px;
	}

	.badge {
		width: 90px;
		height: 20px;
		border-radius: var(--border-radius-full);
	}

	.separator {
		height: var(--border-width-thin);
		background-color: var(--neutral-400);
	}

	.row {
		width: 100%;
		height: 32px;
		flex-shrink: 0;
	}
}

.home-page {
	display: flex;
	height: 100vh;
	width: 100%;

	&:has(.app-sidebar.collapsed) {
		--current-nav-width: var(--width-nav-collapsed);
	}

	&:has(.app-sidebar.side-nav):not(:has(.app-sidebar.collapsed)) {
		--current-nav-width: var(--width-nav-side);
	}

	&:has(.mobile-nav) {
		--current-nav-width: 0px;
	}

	&.is-loading,
	&.is-loading * {
		cursor: wait !important;
	}

	&:has(.app-sidebar.collapsed) .left-panel-container {
		left: var(--width-nav-collapsed);
	}

	&:has(.app-sidebar.side-nav) .home-content {
		left: 0;
	}

	&:has(.app-sidebar.side-nav):not(:has(.app-sidebar.collapsed)) .left-panel-container {
		left: var(--width-nav-side);
	}
}

.home-page:not(:has(.app-sidebar.collapsed)) .project-badge {
	display: none;
}

.view-toggle-floating {
	position: absolute;
	/* aligned with MapSearch.vue's toggle circle top */
	top: var(--spacing-md);
	right: var(--spacing-md);
	z-index: 1;
}

.project-badge {
	position: absolute;
	top: var(--spacing-md);
	left: 46%;
	transform: translateX(-50%);
	z-index: 1;
	display: flex;
	align-items: center;
	gap: var(--spacing-sm);
	padding: var(--spacing-xs) var(--spacing-sm) var(--spacing-xs) var(--spacing-md);
	background-color: var(--neutral-0);
	border: 1px solid var(--neutral-50);
	border-radius: var(--border-radius-2xl);
	box-shadow: var(--shadow-md);
	color: var(--neutral-600);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	max-width: var(--max-width-container);

	.project-badge-name {
		font-weight: var(--font-weight-semibold);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.project-badge-exit {
		display: flex;
		align-items: center;
		justify-content: center;
		background: transparent;
		border: none;
		cursor: pointer;
		color: var(--neutral-600);
		padding: var(--spacing-xxs);
		border-radius: 50%;
		flex-shrink: 0;
		line-height: 0;

		&:hover {
			background-color: var(--neutral-200);
			color: var(--neutral-900);
		}
	}
}

.selection-action-bar {
	position: absolute;
	top: var(--spacing-md);
	left: 50%;
	transform: translateX(-50%);
	z-index: 1;
	display: flex;
	align-items: center;
	gap: var(--spacing-sm);
	padding: var(--spacing-xs) var(--spacing-sm) var(--spacing-xs) var(--spacing-md);
	background-color: var(--neutral-0);
	border: 1px solid var(--neutral-50);
	border-radius: var(--border-radius-2xl);
	box-shadow: var(--shadow-md);
	color: var(--neutral-600);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-semibold);

	button {
		display: flex;
		align-items: center;
		justify-content: center;
		background: transparent;
		border: none;
		cursor: pointer;
		color: var(--neutral-600);
		padding: var(--spacing-xxs);
		border-radius: 50%;
		flex-shrink: 0;
		line-height: 0;

		&:hover {
			background-color: var(--neutral-200);
			color: var(--neutral-900);
		}
	}
}

.home-content {
	left: 240px;
	right: 0;
	top: 0;
	bottom: 0;
	position: fixed;
	transition: left 0.3s ease;

	&:not(:has(.left-panel-container)) :deep(.mapboxgl-ctrl-logo) {
		margin-left: 10px !important;
	}

	@media (max-width: 768px) {
		left: 0;
	}
}

.home-page:has(.app-sidebar.side-nav) .home-content :deep(.mapboxgl-ctrl-logo) {
	margin-left: calc(
		var(--current-nav-width) + var(--left-panel-open, 0) * var(--width-left-panel) + var(--spacing-sm)
	) !important;
	transition: margin-left 0.3s ease;
}

.home-page:has(.mobile-nav) .home-content {
	bottom: calc(var(--space-56) + env(safe-area-inset-bottom));
}

.home-page:has(.mobile-nav) .home-content :deep(.mapboxgl-ctrl-logo) {
	margin-left: 0 !important;
}

.home-page:has(.mobile-nav) .home-content :deep(.mapboxgl-ctrl-bottom-left) {
	left: var(--space-40);
	bottom: 0;
	scale: 0.8;
}

.home-page:has(.mobile-nav) .home-content :deep(.map-controls) {
	bottom: calc(var(--space-64) + env(safe-area-inset-bottom));
}

.home-page.mobile-detail-sheet-open .home-content :deep(.map-controls) {
	display: none;
}

.home-page:has(.mobile-nav) .home-content :deep(.mapboxgl-ctrl-attrib.mapboxgl-compact-show) {
	bottom: calc(var(--space-64) + env(safe-area-inset-bottom));
}

.home-page:has(.mobile-nav) .home-content :deep(.search) {
	left: var(--space-12);
}

.home-page:has(.mobile-nav) .home-content :deep(.search.is-open) {
	width: calc(100vw - 2 * var(--space-12));
}

.home-page:has(.mobile-nav) .right-panel-container {
	height: calc(100% - calc(var(--space-56) + env(safe-area-inset-bottom)));
}

/* dialog is teleported out of .home-page, so :has() is rooted at body instead */
body:has(.home-page .mobile-nav) dialog#selectedOpportunity {
	top: 0;
	margin: 0;
	height: calc(100% - calc(var(--space-56) + env(safe-area-inset-bottom)));
}

.map-container {
	width: 100%;
	height: 100%;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
}

/* keep the collapsed opportunity-detail reopen button from floating on top of the viewport panel */
.home-content:has(.viewport-panel) :deep(.detail-panel-reopen-btn) {
	z-index: 4;
}

.filter-reopen-btn {
	position: fixed;
	/* sits below MapSearch.vue's own toggle circle, aligned to the same left edge, while the panel is closed */
	top: calc(var(--spacing-md) + var(--space-40) + var(--spacing-floating-btn-stack-gap));
	left: calc(var(--current-nav-width, 240px) + var(--spacing-md));
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--space-40);
	height: var(--space-40);
	border-radius: var(--border-radius-full);
	border: none;
	background: var(--neutral-0);
	color: var(--neutral-600);
	cursor: pointer;
	box-shadow: var(--shadow-lg);
	transition:
		opacity 0.15s ease,
		left 0.3s ease;

	&:hover {
		opacity: 1;
	}
}

.viewport-panel-reopen-btn {
	position: fixed;
	/* sits below .view-toggle-floating (top: var(--spacing-md), height: var(--space-40)), same stack gap */
	top: calc(var(--spacing-md) + var(--space-40) + var(--spacing-floating-btn-stack-gap));
	right: var(--spacing-md);
	z-index: 5;
	display: flex;
	align-items: center;
	gap: var(--spacing-xs);
	height: var(--space-40);
	padding: 0 var(--spacing-md);
	border-radius: var(--border-radius-full);
	border: none;
	background: var(--neutral-0);
	color: var(--neutral-900);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-medium);
	cursor: pointer;
	box-shadow: var(--shadow-lg);
	transition: box-shadow 0.15s ease;

	&:hover {
		box-shadow: var(--shadow-xl);
	}

	&.is-standalone {
		top: var(--spacing-md);
	}

	.count {
		display: flex;
		align-items: center;
		justify-content: center;
		min-width: var(--size-icon-lg);
		height: var(--size-icon-lg);
		padding: 0 var(--spacing-xxs);
		border-radius: var(--border-radius-full);
		background-color: var(--primary-green);
		color: var(--neutral-0);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-semibold);
	}
}

/* left-drawer */
.left-panel-container {
	position: fixed;
	top: 0;
	left: 240px;
	z-index: 10;
	width: var(--width-left-panel);
	height: 100%;
	overflow: hidden;
	transition: left 0.3s ease;
	box-shadow: var(--shadow-base-left);

	&.drawer-open {
		min-height: 100%;
	}
}

.drawer-panel {
	background-color: var(--neutral-0);
	padding: var(--spacing-md) var(--spacing-xl);
	display: flex;
	flex-direction: column;
	height: 100%;
	transition: max-height 0.1s ease;

	.header {
		flex-shrink: 0;
		margin-bottom: var(--spacing-lg);
		position: relative;
	}

	.title {
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-medium);
		line-height: var(--line-height-normal);
		margin-bottom: var(--spacing-xs);
	}

	.subtitle {
		color: var(--neutral-600);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-regular);
		line-height: var(--line-height-normal);
	}

	.panel-toggle {
		position: absolute;
		top: var(--spacing-sm);
		right: 0;
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		background: transparent;
		border: none;
		cursor: pointer;
		z-index: 1;
		color: var(--neutral-600);
		transform: rotate(180deg);
		transition:
			transform 0.2s ease-out,
			color 0.2s ease;
		display: flex;
		align-items: center;
		justify-content: center;

		&:hover {
			color: var(--neutral-900);
		}

		.app-icon {
			display: block;
		}
	}

	.content {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		min-height: 0;
		padding-right: var(--spacing-xs);
		margin-right: calc(var(--spacing-xs) * -1);
		scrollbar-width: none;
		-ms-overflow-style: none;
	}
}

.right-panel-container {
	position: fixed;
	top: 0;
	right: 0;
	z-index: 10;
	width: var(--width-right-panel);
	max-width: var(--width-right-panel);
	height: 100%;
	box-shadow: var(--shadow-base-right);
	overflow: hidden;
	transition: right 0.2s ease;

	&.drawer-open {
		min-height: 100%;
	}

	&.with-secondary-panel {
		right: var(--width-secondary-panel);
	}
}

.right-drawer-panel {
	background-color: var(--neutral-0);
	box-shadow: var(--shadow-md);
	padding: var(--spacing-sm) var(--spacing-md);
	display: flex;
	flex-direction: column;
	height: 100%;

	.content {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		min-height: 0;
		padding-right: var(--spacing-xs);
		margin-right: calc(var(--spacing-xs) * -1);
		scrollbar-width: none;
		-ms-overflow-style: none;
	}
}

.detail-panel {
	height: 100%;
	overflow-y: auto;
	position: relative;
	scrollbar-width: none;
}

.panel-close-btn {
	position: absolute;
	top: var(--spacing-sm);
	right: var(--spacing-sm);
	width: 24px;
	height: 24px;
	background: rgba(255, 255, 255, 0.9);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	font-weight: bold;
	color: var(--neutral-600);
	z-index: 10;
	backdrop-filter: blur(4px);
	transition: all 0.2s ease;

	&:hover {
		background: white;
		color: var(--neutral-900);
		box-shadow: var(--shadow-sm);
	}
}

.mobile-banner {
	position: fixed;
	left: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	text-align: center;
	width: 100%;
	margin-bottom: 0;
	padding: var(--spacing-md);
	color: var(--neutral-0);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-base);
	font-weight: var(--font-weight-bold);
	z-index: 10;
	background: var(--neutral-900);
	box-shadow: 0 calc(var(--border-width-thin) * -1) var(--spacing-xs) var(--spacing-xs) var(--neutral-600);
}

.access-denied-message {
	position: fixed;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1000;
	background-color: var(--accent-warning);
	color: var(--neutral-0);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-base);
	font-weight: var(--font-weight-medium);
	padding: var(--spacing-md) var(--spacing-xl);
	border-radius: var(--border-radius-base);
	box-shadow: var(--shadow-md);
	margin-top: var(--spacing-md);
	text-align: center;
}

dialog#selectedOpportunity {
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 100%;
	height: 100%;
	border: none;

	#close-action {
		position: absolute;
		top: var(--spacing-md);
		right: var(--spacing-md);
		display: flex;
		justify-content: center;
		align-items: center;
		cursor: pointer;
		color: var(--neutral-600);

		&:hover {
			color: var(--neutral-900);
		}
	}

	#opportunity-card-detailed {
		margin: var(--spacing-xs);
		border-radius: var(--border-radius-base);
	}
}

dialog:modal {
	max-width: 100vw;
	background-color: var(--neutral-600);
}

:deep(.mapboxgl-ctrl-logo) {
	margin-left: 210px !important;
}

.table-type-selector {
	position: relative;
	display: inline-flex;
	align-items: center;
	align-self: flex-start;
	margin-bottom: var(--spacing-sm);

	select {
		appearance: none;
		background: transparent;
		border: none;
		cursor: pointer;
		font-family: var(--font-family-primary);
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-medium);
		color: var(--neutral-900);
		padding-right: var(--spacing-lg);

		&:focus {
			outline: none;
		}

		&:hover {
			color: var(--neutral-600);
		}
	}

	.table-type-label {
		font-family: var(--font-family-primary);
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-medium);
		color: var(--neutral-900);
	}

	&::after {
		content: "";
		position: absolute;
		right: var(--spacing-xs);
		top: 50%;
		width: var(--spacing-sm);
		height: var(--spacing-sm);
		border-right: var(--border-width-base) solid var(--neutral-600);
		border-bottom: var(--border-width-base) solid var(--neutral-600);
		transform: translateY(-70%) rotate(45deg);
		pointer-events: none;
	}

	&.is-label::after {
		content: none;
	}
}
</style>
